/**
 * The list view of the Activations page — five columns through the shared composite `Table`:
 * Activation (the identifier strip) · Schedule · Last run · Updated · a trailing ⋯ menu.
 *
 * Rows arrive already filtered and sorted (the toolbar owns one sort for both views), so the columns are
 * pure renderers and the table's own header sorting stays off — two sources of sort truth on one screen
 * is a bug, not a feature.
 *
 * **The Audience column is gone, folded into the first one.** An activation is identified by the PAIR —
 * audience → destination audience — and that pair is now drawn by `ActivationStrip`, the same component
 * the detail header, the run instrument and the Activate wizard use. Once the strip names the audience,
 * a separate Audience column three columns over states the same fact twice per row, which is the one
 * thing every other decision on this surface refuses to do. The audience does not become less reachable
 * for it: it is still a search field, a filter facet and a sort key in the toolbar, and it is still on
 * every row — at a fixed left edge, which is a better scan position than the column it left.
 *
 * **The Status column is gone too, folded into the strip's pilot-light dot.** Once the identifier draws
 * the on/off state at the head of every row, a column three over states it a second time — the same
 * argument that retired the Audience column. `activationStatus()` and the Status filter facet are
 * untouched: the fact stays filterable and sortable-by-facet, it just stops being a column. The state
 * still reaches a screen reader, but NOT through the strip — see `activationRowLabel`.
 *
 * **The page keeps its wider `max-w-7xl`.** Dashboards, Workflows and Context Hub sit at `max-w-5xl`;
 * this page is wider because an activation row carries a PAIR (audience → destination audience) plus a
 * cadence, a result and two timestamps, which is more than a dashboard row has to say. Audiences draws
 * real column headers too as of 2026-08-18, so headers are no longer what separates this page from the
 * others — only the width is.
 *
 * A NOTE ON THE MEASUREMENTS THAT USED TO BE HERE. This block previously carried two figures presented
 * as browser measurements: that pulling back to `5xl` clipped nine cells, and that the pane at 1280×800
 * is 734px against 800px of fixed columns. Both were wrong. The first came from a canvas with no app
 * sidebar and was retracted in the PR that added it, without ever being corrected here; the second does
 * not reconcile with the columns below, which sum to more than 800px. They have been deleted rather
 * than restated, because a number in a comment is inherited as fact by whoever reads it next — which is
 * exactly what happened.
 *
 * What survives, and how it was arrived at: every column carries an explicit width, and the total is
 * deterministic rather than negotiated. Left flexible, the first column absorbs the shortfall and
 * collapses toward 0px, which hides the row's identity and makes two different activations render as
 * the same row.
 *
 * The widths — 26.5 + 6.5 + 18.5 + 6.5 + 3.5 = 61.5rem (984px) — were sized against the longest string
 * each column can hold: the widest cadence Lookout can produce, and the longest result string plus
 * dayjs's longest relative time ON ONE LINE ("a few seconds ago", not the "21 hours ago" every
 * screenshot happens to show). Two things about those numbers are worth knowing before you change one:
 *
 * The trailing 3.5rem actions column was paid for out of the first column. Nothing else had slack to
 * give at the time: Last run is sized to its own worst case within a couple of pixels, and Updated is
 * already narrower than its own. The alternative was a table that overflows its pane at the QA width.
 *
 * Dropping the Schedule icon later handed a rem back — that width had been paid for a 14px glyph and
 * its 4px gap, which text does not need — and it went to the identity column rather than being spread,
 * because that is the only column measured to be clipping real content.
 *
 * Measured in the running app at a 990px pane: the columns render 427/105/298/105/56 and the audience
 * track inside the first column is 172px, up from 163px when the ⋯ column landed and still short of
 * the 196px it had before. Long names still clip — "Paying Customers — Upgrade Intent, Gone Quiet"
 * wants 304px — and the full string stays on the strip's `title` and in the row activator's accessible
 * name. If that trade stops being worth it, the width has to come from somewhere real; dropping
 * Updated is the only column with a story for going.
 *
 * 1. Under fixed layout these are NOMINAL widths. Measured in the running app at a 990px pane, the
 *    earlier five-column set rendered 459/121/169/137/105 against a declared 456/120/168/136/104 —
 *    leftover space redistributes proportionally, so columns come out WIDER than declared.
 *    Over-subscribe the pane instead and they hold their declared widths and the wrapper scrolls. The
 *    sum predicts; the browser decides.
 * 2. Schedule was 7rem until it was measured with the webfont actually loaded, at which point
 *    "30 minutes" wanted 74px against 72px of cell. Two pixels. Arithmetic against a font that has not
 *    loaded is not a measurement — `await document.fonts.ready` first, or you will size a column
 *    against a fallback face.
 *
 * Re-measure at 1280×800 with the sidebar expanded, and say so, before writing any new figure here.
 */
import { Pencil, Trash2 } from 'lucide-react';
import { useMemo } from 'react';

import { KebabMenu } from '@/_shared/components/list/KebabMenu';
import { ROW_ACTIONS_COLUMN } from '@/_shared/components/list/listColumns';
import { Table, type TableColumn } from '@/_shared/components/Table';
import { formatDateTimeTitle } from '@/_shared/lib/format';

import {
  activationRowLabel,
  audienceLabel,
  destinationAudienceLabel,
  hasResolvedDestinationName,
  relativeTime,
} from '../lib/activation-list';
import { isRunning, RUN_STATUS_LABEL, runStatus } from '../lib/run-view';
import type { ActivationRow, RawSyncRun } from '../types';
import { ActivationStrip } from './ActivationStrip';
import { ScheduleLabel } from './ScheduleLabel';
import { SyncStatusBadge } from './SyncStatusBadge';

/**
 * The strip is the tallest thing on a row now that nothing stacks — no run-cell lines, no destination
 * sub-line. Measured in the running app at 1280×800: rows are 40–41px, uniform.
 *
 * Only the pre-measurement estimate for virtualization; rows are re-measured on mount. Wrong in the
 * cheap direction is fine, wildly wrong makes the scrollbar lie until the first pass lands.
 */
const ESTIMATED_ROW_HEIGHT = 40;

const getRowId = (row: ActivationRow): string => row.syncId;

/**
 * The last run: how it ended, and when. ONE column, on one line.
 *
 * It was briefly two — a Result column beside a Last run column — and that was the wrong shape. A
 * result is not a peer of a time; it is an ATTRIBUTE of the run the time belongs to. The tell was the
 * empty state: an activation that has never run had to say so twice, "No runs yet" in one column and
 * an em dash in the next, because neither column could stand up without the run. Two columns that
 * cannot be empty independently are one column.
 *
 * What the original complaint was actually about is fixed either way: the cell used to STACK three
 * lines. It now states one line per row, which is what makes a column scannable — and the records
 * ratio that made it three is gone entirely, being detail-page material.
 *
 * Not to be confused with the pipeline's on/off state, which the strip's pilot light carries: a
 * disabled pipeline can still show a run that succeeded, and an enabled one a run that failed. Two
 * genuinely independent axes — which is why this column is not, and must not become, "Status".
 */
function LastRunCell({ run }: { run: RawSyncRun | undefined }): React.ReactElement {
  // Stated ONCE. There is no run, so there is no result and no time either.
  if (!run) return <span className="text-xs text-muted-foreground">No runs yet</span>;

  // A live run states its badge and no time: its own start is not the LAST run's, and a column that
  // means "when it last finished" on one row and "when this one began" on the next means nothing.
  // `formatDateTimeTitle` renders "Invalid Date" for exactly the inputs `relativeTime` rejects, so the
  // time and its title appear together or not at all.
  const startedAgo = isRunning(run) ? null : relativeTime(run.started_at);

  return (
    <span className="flex min-w-0 items-center gap-2">
      <span title={RUN_STATUS_LABEL[runStatus(run)]} className="shrink-0">
        <SyncStatusBadge run={run} />
      </span>
      {startedAgo !== null && (
        <span
          title={`Started ${formatDateTimeTitle(run.started_at)}`}
          className="truncate text-xs text-muted-foreground"
        >
          {startedAgo}
        </span>
      )}
    </span>
  );
}

function buildColumns(
  audienceNames: ReadonlyMap<string, string>,
  onEdit: (row: ActivationRow) => void,
  onDelete: (row: ActivationRow) => void,
  editDisabledReasonFor: (row: ActivationRow) => string | undefined,
  deleteDisabledReason: string | undefined,
): TableColumn<ActivationRow>[] {
  return [
    {
      id: 'activation',
      header: 'Activation',
      headerAriaLabel: 'Audience, destination audience and destination',
      // Explicit, not flexible. Left to absorb the remainder, this column collapses toward 0px once the
      // fixed columns exceed the pane — the destination audience is the one thing the row is about, and
      // it was the thing that vanished. With every column sized the table has a deterministic total and
      // the wrapper scrolls instead of a cell disappearing.
      //
      // Reduced from 31.5rem to pay for the run column. Measured in the running app at 1280×800 with the
      // sidebar expanded, 28.5rem rendered 459px and gave the audience track 192px; long names DO clip
      // there — "High-Value Customers Inactive 14+ Days" wants 263px — and they clipped before this work
      // too, at a track roughly 19px wider. What narrowed is the band of names that just fitted. Folding
      // Result back into Last run handed half a rem back, which comes here rather than anywhere else,
      // because this is the only column measured to be clipping real content.
      // The full string stays reachable: the strip carries it as a `title`, and the row activator's
      // accessible name is the whole relation. See the width note in the file header.
      width: '26.5rem',
      activatesRow: true,
      // Without this the row-activator button's accessible name is the whole cell — now the strip's
      // full "Audience X activates into Destination audience Y" reading plus the sub-line. It names the
      // WHOLE relation, since that is what the cell draws; an `aria-label` replaces the contents, so
      // the strip's own screen-reader wording does not double up inside it.
      activatesRowLabel: (row) => activationRowLabel(row, audienceLabel(row, audienceNames)),
      wrap: true,
      // All-phrasing (`<span>`s / `<img>`): under `activatesRow` this cell renders inside a `<button>`,
      // which is also why the strip runs with `tooltips={false}` — a tooltip trigger inside the row
      // activator is a second tab stop. `title` recovers the full string instead.
      // The destination sub-line is GONE from the table row (2026-08-19). It named the destination the
      // far side lives in — on a Lookout-minted destination, the platform, which the strip's own brand
      // mark already states one line up. So on most rows it was a second, wordier copy of a fact the
      // row had already made, and it cost every row a second line.
      //
      // The card view keeps it: a card has vertical room a table row does not, and its meta line is
      // where a card carries this kind of qualifier.
      cell: (row) => (
        <ActivationStrip
          size="md"
          tooltips={false}
          // The surface the whole alignment request is about: every row renders into the same
          // fixed-width column, so identical `fr` shares are identical pixels and the arrows form a
          // column by construction rather than by measurement.
          layout="tracks"
          // The pilot light, and the reason there is no Status column to its right any more.
          enabled={row.enabled}
          audienceLabel={audienceLabel(row, audienceNames)}
          warehouseType={row.warehouseType}
          destinationLabel={destinationAudienceLabel(row)}
          destinationDefinitionName={row.destination?.definitionName}
          // Rungs two and three of the ladder are an id and a destination name, not the audience's
          // own name — set as an identifier so the row reads "we do not have a name for this yet"
          // rather than "this audience is called 23847…".
          destinationIsIdentifier={!hasResolvedDestinationName(row)}
        />
      ),
    },
    {
      id: 'schedule',
      header: 'Schedule',
      // Sized for the widest cadence Lookout can create. The nine options it offers top out at
      // "30 minutes"; a connection edited directly in RudderStack can carry any interval, and that is
      // what the title below is for.
      //
      // Narrowed 7.5 → 6.5rem when the icon came out: that width included the glyph's 14px and its 4px
      // gap, which the text does not need. The rem freed goes to the identity column, which is the one
      // column measured to be clipping real content.
      width: '6.5rem',
      wrap: true,
      // The cadence itself, its muting when disabled, and the absence of an icon are all
      // `ScheduleLabel`'s business — including WHY a paused sync still states its cadence rather than
      // an em dash. Restating any of that here is how the table and the card came to disagree.
      cell: (row) => <ScheduleLabel schedule={row.schedule} enabled={row.enabled} compact className="text-sm" />,
    },
    {
      id: 'lastRun',
      header: 'Last run',
      // Sized for the worst case ON ONE LINE: the longest of the badge's four strings ("Completed with
      // failures") plus dayjs's longest relative string ("a few seconds ago", not the "21 hours ago"
      // shape every screenshot happens to show) plus the gap between them.
      width: '18.5rem',
      wrap: true,
      cell: (row) => <LastRunCell run={row.lastRun} />,
    },
    {
      id: 'updated',
      header: 'Updated',
      width: '6.5rem',
      wrap: true,
      cell: (row) => {
        const { updatedAt } = row;
        const updatedAgo = relativeTime(updatedAt);
        // `formatDateTimeTitle` would render "Invalid Date" for the same inputs `relativeTime` rejects,
        // so the title exists only on the branch where the timestamp actually parsed.
        if (updatedAt === undefined || updatedAgo === null) {
          return <span className="text-xs text-muted-foreground">—</span>;
        }
        return (
          <span title={formatDateTimeTitle(updatedAt)} className="block truncate text-xs text-muted-foreground">
            {updatedAgo}
          </span>
        );
      },
    },
    {
      ...ROW_ACTIONS_COLUMN,
      cell: (row) => (
        <KebabMenu
          // Named by the row, not "Actions" — a screen reader hearing eleven identical "Actions"
          // buttons cannot tell which activation it is about to delete.
          label={`Actions for ${destinationAudienceLabel(row)}`}
          actions={[
            // Edit above Delete: the reversible action comes first, and the destructive one stays at
            // the bottom edge where a mis-click is least likely to land on it.
            {
              label: 'Edit',
              icon: Pencil,
              disabled: editDisabledReasonFor(row) !== undefined,
              disabledReason: editDisabledReasonFor(row) ?? '',
              onSelect: () => onEdit(row),
            },
            {
              label: 'Delete',
              icon: Trash2,
              destructive: true,
              disabled: deleteDisabledReason !== undefined,
              disabledReason: deleteDisabledReason ?? '',
              onSelect: () => onDelete(row),
            },
          ]}
        />
      ),
    },
  ];
}

interface ActivationsTableProps {
  /** Already filtered + sorted, and STABLE across renders — `Table` keys `resetScrollOnDataChange` on identity. */
  rows: ActivationRow[];
  audienceNames: ReadonlyMap<string, string>;
  onOpen: (row: ActivationRow) => void;
  onEdit: (row: ActivationRow) => void;
  onDelete: (row: ActivationRow) => void;
  /**
   * Why `Edit` is unavailable, or `undefined` when it is not. See `ActivationsList.tsx`.
   *
   * A REASON, not a `demo` boolean: the boolean folded the still-unknown window into "demo", so a real
   * workspace was told "not available in the demo workspace" while `workspace.get` was in flight.
   * Per row, because a row whose audience needs repair refuses Edit on its own (ACT2-1286).
   */
  editDisabledReasonFor: (row: ActivationRow) => string | undefined;
  /** Why `Delete` is unavailable, or `undefined` when it is not: an off sibling (ACT2-1124 LLD §5.9). */
  deleteDisabledReason: string | undefined;
}

export function ActivationsTable({
  rows,
  audienceNames,
  onOpen,
  onEdit,
  onDelete,
  editDisabledReasonFor,
  deleteDisabledReason,
}: ActivationsTableProps): React.ReactElement {
  const columns = useMemo(
    () => buildColumns(audienceNames, onEdit, onDelete, editDisabledReasonFor, deleteDisabledReason),
    [audienceNames, onEdit, onDelete, editDisabledReasonFor, deleteDisabledReason],
  );

  return (
    <Table
      data={rows}
      columns={columns}
      getRowId={getRowId}
      onRowClick={onOpen}
      estimateRowHeight={ESTIMATED_ROW_HEIGHT}
      fixedLayout
      fillHeight
    />
  );
}
